import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import * as d3 from 'd3';

interface GraphSeriesPoint {
  name: string;
  value: number;
}

interface GraphSeries {
  name: string;
  series: GraphSeriesPoint[];
}

@Component({
  selector: 'app-graph',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DataViewGraphComponent implements OnInit, OnChanges {
  private readonly colorDomain = ['#5AA454', '#A10A28', '#C7B42C', '#AAAAAA'];
  private readonly xAxisLabel = 'Period';

  @Output() closeGraph = new EventEmitter<boolean>();

  @Input() height = 300;
  /**
   * Vertical space (px) to reserve at the bottom of the graph for an element
   * that overlaps it, such as a fixed navbar. The rendered SVG is capped to
   * `height - bottomOffset` so its lower edge stays visible when it scales up
   * to its maximum height.
   */
  @Input() bottomOffset = 0;
  @Input() width = 1000;
  @Input() yAxisLabel = 'CURRENCY';
  @Input() graphItems: GraphSeries[] = [{ name: '', series: [] }];

  @ViewChild('chart', { static: true }) private chartContainer: ElementRef;

  ngOnInit(): void {
    this.createChart();
  }

  ngOnChanges(): void {
    if (!this.chartContainer) {
      return;
    }
    this.createChart();
  }

  private createChart(): void {
    const element = this.chartContainer.nativeElement;
    d3.select(element).selectAll('svg').remove();

    const series = (this.graphItems || []).filter(
      (s) => s && Array.isArray(s.series) && s.series.length > 0,
    );
    if (series.length === 0) {
      return;
    }

    const margin = { top: 20, right: 30, bottom: 50, left: 100 };
    const width = this.width - margin.left - margin.right;
    const height = this.height - margin.top - margin.bottom;

    const xLabels: string[] = [];
    const seen = new Set<string>();
    for (const s of series) {
      for (const point of s.series) {
        if (!seen.has(point.name)) {
          seen.add(point.name);
          xLabels.push(point.name);
        }
      }
    }

    const allValues = series.flatMap((s) => s.series.map((p) => p.value));
    const yMin = Math.min(0, d3.min(allValues) ?? 0);
    const yMax = Math.max(0, d3.max(allValues) ?? 0);

    const xScale = d3
      .scalePoint<string>()
      .domain(xLabels)
      .range([0, width])
      .padding(0.5);

    const yScale = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .nice()
      .range([height, 0]);

    const colorScale = d3
      .scaleOrdinal<string, string>()
      .domain(series.map((s) => s.name))
      .range(this.colorDomain);

    const renderedHeight = Math.max(this.height - this.bottomOffset, 0);

    const svg = d3
      .select(element)
      .append('svg')
      .attr('width', '100%')
      .attr('height', renderedHeight)
      .attr('preserveAspectRatio', 'xMinYMin meet')
      .attr(
        'viewBox',
        `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`,
      )
      .classed('svg-content-responsive', true);

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    g.append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(xScale));

    g.append('g')
      .attr('class', 'axis axis--y')
      .call(d3.axisLeft(yScale).ticks(5));

    g.append('text')
      .attr('class', 'axis-label axis-label--x')
      .attr('text-anchor', 'middle')
      .attr('x', width / 2)
      .attr('y', height + margin.bottom - 10)
      .text(this.xAxisLabel);

    g.append('text')
      .attr('class', 'axis-label axis-label--y')
      .attr('text-anchor', 'middle')
      .attr('transform', `rotate(-90)`)
      .attr('x', -(height / 2))
      .attr('y', -(margin.left - 20))
      .text(this.yAxisLabel);

    const lineGenerator = d3
      .line<GraphSeriesPoint>()
      .defined((d) => d.value !== null && d.value !== undefined)
      .x((d) => xScale(d.name) ?? 0)
      .y((d) => yScale(d.value));

    for (const s of series) {
      const color = colorScale(s.name);

      g.append('path')
        .datum(s.series)
        .attr('class', 'graph-line')
        .attr('fill', 'none')
        .attr('stroke', color)
        .attr('stroke-width', 2)
        .attr('d', lineGenerator);

      g.selectAll(null)
        .data(s.series.filter((p) => p.value !== null && p.value !== undefined))
        .enter()
        .append('circle')
        .attr('class', 'graph-point')
        .attr('cx', (d) => xScale(d.name) ?? 0)
        .attr('cy', (d) => yScale(d.value))
        .attr('r', 3)
        .attr('fill', color)
        .append('title')
        .text((d) => `${s.name}\n${d.name}: ${d.value}`);
    }
  }
}
